import { Injectable } from '@angular/core';
import {
  AbstractTimeService,
  addHours,
  addMinutes,
  addSeconds,
  IsoTime,
  IsoTimeTz,
} from '@softline/core';
import { TimeParser } from './time.parser';

@Injectable()
export class IsoTimeParser<T extends IsoTime | IsoTimeTz> extends TimeParser<T> {
  private format = 'HH:mm';

  constructor(private timeService: AbstractTimeService<T>) {
    super();
  }

  parse(value: string): T | null {
    try {
      const time =
        this.parseNowInput(value) ||
        this.parseHourInput(value) ||
        this.parseMinutesInput(value) ||
        this.parseLocaleInput(value);
      if (!time || !this.timeService.isValid(time)) return null;
      return time;
    } catch (message) {
      return null;
    }
  }

  private parseNowInput(value: string): string | null {
    if (value === 'j') return this.timeService.now();
    return null;
  }

  private parseHourInput(value: string): string | null {
    const match = /^\d{1,2}$/g.exec(value);
    if (match == null) return null;
    const hours = +match[0];
    if (hours > 24) throw Error('Invalid Hour not more than 24 hours possible');
    let time = this.timeService.midnight();
    time = addHours(time, hours);
    return time;
  }

  private parseMinutesInput(value: string): string | null {
    const match = /^(\d{1,2})(\d{2})$/g.exec(value);
    if (match == null) return null;

    const hours = +match[1];
    const minutes = +match[2];
    if (hours >= 24) throw Error('Invalid Hour not more than 24 hours possible');
    if (minutes > 59) throw Error('Invalid Hour not more than 59 minutes possible');
    let time = this.timeService.midnight();
    time = addHours(time, hours);
    time = addMinutes(time, minutes);
    return time;
  }

  private parseLocaleInput(value: string): string | null {
    const match = /^(\d{1,2}):(\d{1,2})(:(\d{2}))?$/g.exec(value);
    if (match == null) return null;

    const hour = +match[1];
    const minute = +match[2];
    const second = +(match[4] ?? 0);

    if (hour < 0 || hour > 24)
      throw Error(`Invalid hour "${hour}". Hour has to be between 0 and 24.`);
    if (minute < 0 || minute > 59)
      throw Error(`Invalid minute "${minute}". minute has to be between 0 and 59.`);
    if (second && (second < 0 || second > 59))
      throw Error(`Invalid second "${second}". second has to be between 0 and 59.`);

    let time = this.timeService.midnight();
    time = addHours(time, hour);
    time = addMinutes(time, minute);
    time = addSeconds(time, second);
    return time;
  }
}
